import * as React from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Mail, ShieldCheck } from 'lucide-react';
import { post } from '@/lib/api';
import { ME_KEY, isSignedIn, useMe } from '@/lib/auth';
import { useT, useLocale } from '@/i18n';
import { useErrorText } from '@/hooks/useErrorText';
import { useOnline } from '@/hooks/useSync';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Card } from '@/components/ui/card';

/** Email OTP login (spec §10.1). Generic responses — the page never reveals whether an address is allowed. */
export function LoginPage() {
  const t = useT();
  const { locale, setLocale } = useLocale();
  const errText = useErrorText();
  const online = useOnline();
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next');
  const safeNext = next && next.startsWith('/') && !next.startsWith('//') ? next : '/';

  const [step, setStep] = React.useState<'email' | 'code'>('email');
  const [email, setEmail] = React.useState('');
  const [code, setCode] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [cooldown, setCooldown] = React.useState(0);
  const codeRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  if (isSignedIn(me)) return <Navigate to={safeNext} replace />;

  const requestCode = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const r = await post<{ cooldownSeconds: number }>('/auth/request-code', { email });
      setCooldown(r.cooldownSeconds);
      setStep('code');
      setTimeout(() => codeRef.current?.focus(), 50);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e?: React.FormEvent, value = code) => {
    e?.preventDefault();
    if (!/^\d{6}$/.test(value)) return;
    setError(null);
    setBusy(true);
    try {
      await post('/auth/verify-code', { email, code: value });
      await qc.invalidateQueries({ queryKey: ME_KEY });
      await qc.fetchQuery({ queryKey: ME_KEY });
      navigate(safeNext, { replace: true });
    } catch (err) {
      setError(errText(err));
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-paper px-4 py-10 pt-safe">
      <div className="mb-8 flex flex-col items-center gap-3 text-center">
        <div className="grid size-14 place-items-center rounded-2xl bg-primary text-[28px] font-semibold text-primary-ink shadow-md greek">α</div>
        <h1 className="text-[26px] font-semibold tracking-tight">{t('app.name')}</h1>
        <p className="greek max-w-sm text-[16px] text-muted" lang="el">
          Γράφω, διορθώνω, μαθαίνω.
        </p>
      </div>
      <Card className="w-full max-w-[400px] p-6 sm:p-7">
        {step === 'email' ? (
          <form onSubmit={requestCode} className="space-y-4" noValidate>
            <div>
              <h2 className="text-[18px] font-semibold">{t('auth.title')}</h2>
              <p className="mt-1 text-[13.5px] text-muted">{t('auth.subtitle')}</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                data-testid="login-email"
              />
            </div>
            {error && <p className="text-[13px] text-danger" role="alert">{error}</p>}
            {!online && <p className="text-[13px] text-warn">{t('auth.needsNetwork')}</p>}
            <Button type="submit" className="w-full" loading={busy} disabled={!email.includes('@') || !online} data-testid="login-request">
              <Mail /> {t('auth.sendCode')}
            </Button>
          </form>
        ) : (
          <form onSubmit={verify} className="space-y-4">
            <button type="button" onClick={() => setStep('email')} className="inline-flex items-center gap-1 text-[13px] text-muted hover:text-ink">
              <ArrowLeft className="size-4" /> {t('auth.changeEmail')}
            </button>
            <div>
              <h2 className="text-[18px] font-semibold">{t('auth.codeTitle')}</h2>
              <p className="mt-1 text-[13.5px] text-muted">{t('auth.codeSent', { email })}</p>
            </div>
            <Input
              ref={codeRef}
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              value={code}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                setCode(v);
                if (v.length === 6) void verify(undefined, v);
              }}
              className="h-14 text-center font-mono text-[26px] tracking-[0.5em]"
              aria-label={t('auth.code')}
              data-testid="login-code"
            />
            {error && <p className="text-[13px] text-danger" role="alert">{error}</p>}
            <Button type="submit" className="w-full" loading={busy} disabled={code.length !== 6} data-testid="login-verify">
              <ShieldCheck /> {t('auth.verify')}
            </Button>
            <div className="text-center text-[13px] text-muted">
              {cooldown > 0 ? (
                t('auth.resendIn', { s: cooldown })
              ) : (
                <button type="button" className="text-primary hover:underline" onClick={() => void requestCode()}>
                  {t('auth.resend')}
                </button>
              )}
            </div>
          </form>
        )}
      </Card>
      <p className="mt-6 max-w-[400px] text-center text-[12px] leading-relaxed text-muted">{t('auth.privateNote')}</p>
      <div className="mt-4 flex gap-2 text-[12px]">
        <button className={locale === 'ru' ? 'font-semibold text-ink' : 'text-muted'} onClick={() => setLocale('ru')}>
          Русский
        </button>
        <span className="text-muted-2">·</span>
        <button className={locale === 'en' ? 'font-semibold text-ink' : 'text-muted'} onClick={() => setLocale('en')}>
          English
        </button>
      </div>
    </div>
  );
}
